/**
 * useOsmdInteractiveScore
 *
 * Full-featured OpenSheetMusicDisplay renderer for the interactive main preview.
 * Unlike {@link useOsmdRenderer} (lightweight, read-only single-measure tiles), this owns a
 * fully configured OSMD instance with cursors, coloring and lyrics.
 *
 * **Orchestrator:** Instanz-Lifecycle (create/render/zoom/dispose) + Selektions-State leben hier;
 * die Fachlogik ist in sieben Sub-Composables unter `composables/osmd/` zerlegt und wird hier zu
 * genau EINEM {@link ScoreEditContext} zusammengesteckt (provide/inject – kein Prop-Drilling):
 * - {@link useOsmdPlayback}      – Audio-Player (osmd-extended), Transport, Tempo, Metronom, Mixer.
 * - {@link useScoreHitTesting}   – Klick → Takt/Note, Identitäts-Lookups (Takt+Index → GraphicalNote).
 * - {@link useScoreHighlight}    – Takt-/Note-Cursor, rotes SVG-Auswahl-Highlight, Panel-Position.
 * - {@link useScoreSelection}    – Klick-Behandlung, Takt↔Note-Auswahlmodell, Panels öffnen, Restore.
 * - {@link useScoreDragSelection} – Rubber-Band-Mehrfachauswahl (Rechteck-Drag; Commit über
 *   `useScoreSelection.applyDragSelection`, Handler nur im Rückgabewert, nicht im Kontext).
 * - {@link useScoreEditActions}  – Note/Akkord/Tonart-Edits + Copy/Paste/Tie (mutieren `musicXml`).
 * - {@link useLyricEntry}        – Gesangstext-Eingabesitzung (Silbe tippen, Leertaste = weiter).
 *
 * `ScoreEditToolbar`/`NoteActionsOverlay`/`PlaybackControlsOverlay` consume the context through inject;
 * `MusicXmlPreview` stays thin (file loading, toolbar, undo/redo, zoom).
 *
 * The instance is created once the container element exists, re-renders (debounced) whenever the
 * MusicXML changes, applies zoom reactively, and is disposed on unmount to avoid leaking SVG/DOM
 * nodes.
 */
import {computed, type MaybeRefOrGetter, onBeforeUnmount, type Ref, ref, shallowRef, toValue, watch} from 'vue';
import {useElementSize, useEventListener, watchDebounced} from '@vueuse/core';
import {effectiveZoom} from './osmd/effectiveZoom';
import {findPatternMatches} from './osmd/patternMatch';
import {
    ColoringModes,
    type CursorOptions,
    CursorType,
    GraphicalNote,
    OpenSheetMusicDisplay,
} from 'opensheetmusicdisplay';
import {
    type BarlineSide,
    bassPreviewStaffIndices,
    bassPreviewStatus,
    type ClipboardNote,
    getChordFromMeasure,
    getMeasureStructureInfo,
    getKeySignatureFromMeasure,
    getTimeSignatureFromMeasure,
    type KeySignatureInfo,
    measureIsOverfullForMeter,
    type NoteInfo,
    type MeasureStructureInfo,
    type PickupState,
    pickupState,
    readBassPreviewStyles,
    type TimeSignatureInfo,
} from '@/composables/musicXmlDom';
import {music21NameOfGraphicalNote} from '@/composables/osmd/graphicalNoteInfo';
import {type EditTab, provideScoreEditContext, type ScoreEditContext} from '@/components/musicxml-preview/editContext';
import {provideScorePlayback} from '@/components/musicxml-preview/playbackContext';
import type {ActionScope} from '@/components/musicxml-preview/noteActionsMenu';
import {useOsmdPlayback} from '@/composables/osmd/useOsmdPlayback';
import {useScoreHitTesting} from '@/composables/osmd/useScoreHitTesting';
import {useScoreHighlight} from '@/composables/osmd/useScoreHighlight';
import {useScoreLabelColors} from '@/composables/osmd/useScoreLabelColors';
import {useScoreDragSelection} from '@/composables/osmd/useScoreDragSelection';
import {useScoreSelection} from '@/composables/osmd/useScoreSelection';
import {type ScoreNotificationType, useScoreEditActions} from '@/composables/osmd/useScoreEditActions';
import {useLyricEntry} from '@/composables/osmd/useLyricEntry';
import {NOOP_PREVIEW_TONE, type NotePreviewTone} from '@/composables/osmd/notePreviewTone';
import {usePlaybackStore} from '@/stores/playbackStore';
import {lockScrollContainer, neutralizeScrollIntoView} from '@/shared/scrollLock';

// Re-Export: Konsumenten (editContext, OsmdPlaybackControls) importieren den Mixer-Track-Typ
// weiterhin von hier.
export type {MixerTrack} from '@/composables/osmd/useOsmdPlayback';

export interface UseOsmdInteractiveScoreOptions {
    zoom: MaybeRefOrGetter<number>;
    /** Audio-Player für DIESE Vorschau anlegen. Default `false` – die Griffschrift-AUSGABE ist
     *  keine spielbare Partitur (Knopf-Notation, Balgrichtung, Bass-Lyrics, rote Pausen für
     *  unspielbare Töne) und klänge zwangsläufig falsch. Ohne das Flag entsteht KEIN
     *  `PlaybackManager` und es werden KEINE SoundFonts geladen; `ScorePlaybackContext` wird
     *  dann gar nicht bereitgestellt (siehe `components/musicxml-preview/playbackContext.ts`). */
    enablePlayback?: boolean;
    /** Wird am Ende einer Gesangstext-Sitzung gerufen, wenn Text geschrieben wurde – der Parent
     *  übernimmt das Ergebnis dann EINMAL nach oben (siehe `useLyricEntry`, Festlegung 1). */
    onLyricsCommitted?: (musicXml: string) => void;
    /** Spielweise eines Takts im Bass-System geändert – die Vorschau meldet nur, der Aufrufer
     *  baut das System neu auf (sie ruft selbst keine Endpunkte). */
    onBassStyleChange?: (measureIndex: number, style: string) => void;
    /** Taktart ab Takt-Index `measureIndex` ändern und die Noten neu aufteilen lassen – die
     *  Vorschau meldet nur, der Aufrufer ruft den Endpunkt. Ohne Callback bietet der
     *  Taktart-Editor nur die reine Notationskorrektur an. */
    onRebar?: (measureIndex: number, beats: number, beatType: number) => void;
    /** Warum das Neuaufteilen gerade gesperrt ist (`''` = erlaubt), z. B. ungespeicherte Edits. */
    rebarBlockedReason?: MaybeRefOrGetter<string>;
    /** Benutzer-Hinweis (Toast) aus den Sub-Composables, z. B. bei unauflösbarer Notenadresse
     *  (`'error'`, Default) oder als Ergebnis einer Edit-Aktion („Haltebögen zusammenfassen"). */
    notify?: (message: string, type?: ScoreNotificationType) => void;
    /** Löschen darf die Folgenoten nachrücken lassen und fragt dann „Pause oder nachrücken?" –
     *  nur auf der Eingabe-Vorschau (Begründung an `ScoreEditActionsDeps.enableShiftDelete`). */
    enableShiftDelete?: boolean;
    /** Tonhöhen in music21-Schreibweise (`"B-3"`, `"F#4"`), die auf der Harmonika NICHT existieren –
     *  Quelle ist `AnalysisResult.unmappable_notes`. Jede gerenderte Note mit dieser Tonhöhe wird
     *  rot markiert. Nur für die EINGANGS-Vorschau gedacht: in der Griffschrift-Ausgabe stehen
     *  unspielbare Töne bereits als rote Pausen, und deren notierte Tonhöhe ist die Staff-Position
     *  des Knopfes – ein Abgleich wäre dort schlicht falsch. */
    unplayableNotes?: MaybeRefOrGetter<string[] | undefined>;
}

/**
 * Präziser Zugriff auf den (protected) internen Interaktions-Manager des SheetRenderingManager.
 * `displayInteractionManager` liegt als protected-Feld auf `AbstractZoomView`; sein `Dispose()`
 * ist public (`AbstractDisplayInteractionManager`). Kein `any` – exakt getypter Cast-Träger.
 *
 * `disposeResizeListener` ist ein protected-Feld des `WebDisplayInteractionManager`, das ausschließlich
 * in dessen `initialize()` gesetzt wird. Der Konstruktor registriert die DOM-Listener zwar bereits (via
 * `listenForInteractions()`), OSMD ruft `initialize()` aber ggf. erst später auf – zu unserem
 * Dispose-Zeitpunkt ist `disposeResizeListener` daher noch `undefined`. Da `dispose()` es als ERSTES
 * (vor dem Entfernen der DOM-Listener) unbedingt aufruft, wirft es sonst `is not a function` und die
 * eigentliche Listener-Bereinigung läuft nie. Wir typen das Feld hier optional mit, um vor `Dispose()`
 * einen No-op-Fallback setzen zu können.
 */
interface OsmdInteractionCarrier {
    displayInteractionManager?: {
        Dispose: () => void;
        Initialize?: unknown;
        disposeResizeListener?: unknown;
        registerScrollOffsets?: unknown;
    };
}

/**
 * Bereits per `Dispose()` abgehängte Interaktions-Manager. `Dispose()` darf pro Manager-Instanz
 * nur EINMAL laufen: OSMDs `disposeResizeListener` (ResizeObserver-Variante) setzt beim ersten
 * Dispose `resizeObserver = undefined`, bleibt danach aber eine Funktion – der No-op-Guard unten
 * greift also nicht, und ein zweiter `Dispose()` wirft `Cannot read properties of undefined
 * (reading 'disconnect')`. Da `disableOsmdInternalInteraction` idempotent nach jedem Render
 * läuft, erschien die Warnung sonst bei jedem Folgerender. Legt OSMD einen NEUEN Manager an,
 * steht der nicht im Set und wird normal disposed.
 */
const disposedInteractionManagers = new WeakSet<object>();

/**
 * OSMDs interne DOM-Interaktion vollständig abschalten.
 *
 * Der OSMD-KONSTRUKTOR legt immer (unabhängig von einem PlaybackManager) einen
 * `WebDisplayInteractionManager` + `WebSheetRenderingManager` an; der Manager bindet eigene
 * DOM-Listener (mousedown/mousemove/resize) an den Container, und **jedes `osmd.load()`** ruft
 * am Ende `interactionManager.Initialize()`, das zusätzlich einen ResizeObserver anlegt und via
 * `registerScrollOffsets()` einen **`scroll`-Listener an jeden scrollbaren Vorfahren** hängt.
 *
 * Zwei Crash-Pfade, beide "Cannot read properties of undefined (reading '0')":
 * 1. Maus-Event auf LEERER grafischer Partitur (mitten im Re-Render nach `clear()`, oder Render
 *    wegen Container-Breite 0 übersprungen): `getPositionInUnits`/`getClickPosition` greift auf
 *    `graphicalMusicSheet.MusicPages[p].MusicSystems[0]` zu. Die Flags `SingleTouchDisabled`/
 *    `DoubleTouchDisabled` guarden NUR `unitPosTouched`/`unitPosDoubleTouched`, laufen also NACH
 *    der Koordinaten-Umrechnung und verhindern diesen Crash NICHT (decken aber den älteren
 *    "reading 'PositionAndShape'"-Crash ab, daher weiterhin gesetzt).
 * 2. VERWAISTE Scroll-Listener (Stack-Frame `HTMLElement.i3`): `registerScrollOffsets()` meldet
 *    bei jedem `Initialize()` neu an, OHNE die alten Listener abzumelden – `scrollCallbackMap.set`
 *    überschreibt nur den Map-Eintrag, der alte DOM-Listener bleibt hängen und ist nie mehr
 *    erreichbar. `Dispose()` entfernt nur die gemerkten Callbacks und leert `parentScrollMap` –
 *    danach wirft jeder verwaiste Listener beim nächsten Scrollen (`parentScrollMap.get(this)`
 *    → undefined → `[0]`). Ein Dispose-nach-jedem-Render kann Waisen prinzipiell nicht erreichen.
 *
 * Die App macht ihr gesamtes Interaktions-Handling ohnehin selbst (handleContainerClick am
 * Container-Div, eigenes Hit-Testing, eigener reaktiver Zoom), daher wird OSMDs interner
 * Interaktions-Manager per `Dispose()` komplett abgehängt (entfernt die DOM-Listener) und danach
 * werden BEIDE Registrierungspfade als No-op überschrieben: `Initialize` (der `load()`-Pfad) und
 * `registerScrollOffsets` (der `resizeCallback`-`setTimeout(500)`, der ein `Dispose()` überleben
 * und danach neu registrieren würde). So entstehen Waisen gar nicht erst. Idempotent – nach jedem
 * Render erneut aufrufbar. Gehört direkt nach die OSMD-Konstruktion JEDER Instanz (auch der
 * leichten in `useOsmdRenderer`/`usePdfExport` – Guard: `disableOsmdInternalInteraction.spec.ts`).
 * Der Playback-Cursor-Follow (timing-source-getrieben) und das reaktive Resize-Re-Render
 * (`useElementSize(containerRef)`, siehe createInstance/Resize-Watcher) bleiben davon unberührt.
 */
export function disableOsmdInternalInteraction(osmd: OpenSheetMusicDisplay): void {
    const renderingManager = osmd.RenderingManager;
    if (!renderingManager) return;
    renderingManager.SingleTouchDisabled = true;
    renderingManager.DoubleTouchDisabled = true;
    const carrier = renderingManager as unknown as OsmdInteractionCarrier;
    const manager = carrier.displayInteractionManager;
    if (!manager) return;
    // Dispose-once pro Manager-Instanz – ein zweiter Dispose würde in OSMDs
    // `disposeResizeListener` auf dem bereits genullten `resizeObserver` werfen (siehe
    // `disposedInteractionManagers`).
    if (disposedInteractionManagers.has(manager)) return;
    // `dispose()` ruft `disposeResizeListener()` unbedingt als erstes auf – ist `initialize()` noch
    // nicht gelaufen, ist das Feld undefined und Dispose() würde werfen, bevor die DOM-Listener
    // entfernt werden. No-op-Fallback setzen, damit die Listener-Bereinigung durchläuft.
    if (typeof manager.disposeResizeListener !== 'function') {
        manager.disposeResizeListener = () => {
        };
    }
    try {
        manager.Dispose();
    } catch (e) {
        console.warn('[MusicXML Preview] Failed to dispose OSMD interaction manager', e);
    } finally {
        // Auch nach einem Fehlschlag nicht erneut versuchen: der Zustand des Managers ändert
        // sich zwischen Rendern nicht, jeder weitere Versuch würde nur dieselbe Warnung loggen.
        disposedInteractionManagers.add(manager);
        // Beide Re-Registrierungspfade kappen (siehe Docstring, Crash-Pfad 2): jedes spätere
        // `osmd.load()` ruft `Initialize()`, und ein noch ausstehender resizeCallback-Timeout
        // ruft `registerScrollOffsets()` – beides würde die eben entfernten Scroll-Listener/
        // ResizeObserver neu anlegen und beim übernächsten Zyklus verwaiste Listener hinterlassen.
        manager.Initialize = () => {};
        manager.registerScrollOffsets = () => {};
    }
}

/**
 * Neutralisiert `scrollIntoView` auf OSMDs Cursor-`<img>`-Elementen – die einzige Stelle, an der
 * OSMD von sich aus die Seite zur Partitur zieht: `Cursor.reset()` (läuft bei JEDER Notenauswahl
 * über `useScoreHighlight.positionNoteCursor`) geht über `updateWithTimestamp()`, dessen
 * `scrollIntoView({block:'center'})` allein an `osmd.FollowCursor` hängt – das per-Cursor
 * `follow: false` greift dort NICHT.
 *
 * `followCursor: false` (Konstruktor) ist die erste Verteidigungslinie, das hier die zweite: eine
 * Konfigurations-Zusage ist schon einmal zurückgefallen (`528064c`).
 *
 * **Muss nach JEDEM Render laufen** (idempotent): OSMDs `render()` ruft `enableOrDisableCursors()`,
 * das jeden Cursor `Dispose()`t und per `new Cursor(...)` ein frisches `<img>` anlegt – die
 * Neutralisierung des alten Elements ist danach weg. `osmd.cursors`/`Cursor.cursorElement` sind
 * public typisiert, daher ohne Cast erreichbar.
 *
 * Setzt zusätzlich `draggable = false`: `<img>` ist per Browser-Default draggable, und ein per
 * Klick-Halten-Ziehen gezogenes Cursor-Bild meldet `dataTransfer.types: ['Files']` → das
 * window-weite Upload-Overlay erschiene (siehe `shared/windowFileDrag.ts`). Erste
 * Verteidigungslinie ist das `@dragstart.prevent` am Score-Container (`MusicXmlPreview.vue`);
 * das hier hält auch, falls OSMD die Cursor je außerhalb des Containers einhängt.
 */
function disableOsmdCursorScrolling(osmd: OpenSheetMusicDisplay): void {
    for (const cursor of osmd.cursors ?? []) {
        if (cursor?.cursorElement) {
            neutralizeScrollIntoView(cursor.cursorElement);
            cursor.cursorElement.draggable = false;
        }
    }
}

export function useOsmdInteractiveScore(
    musicXml: Ref<string | null>,
    containerRef: Ref<HTMLElement | null>,
    scrollContainerRef: Ref<HTMLElement | null>,
    options: UseOsmdInteractiveScoreOptions,
) {
    const {zoom, enablePlayback = false, unplayableNotes} = options;

    const error = ref<string | null>(null);
    // Mehrteilig? (mehrere Notenzeilen/Parts) – nach jedem Render aus OSMD abgeleitet; steuert die
    // Part-Erweiterungs-Buttons. OSMD ist nicht reaktiv, daher imperativ gesetzt statt computed.
    const hasMultipleParts = ref(false);

    let osmd: OpenSheetMusicDisplay | null = null;
    // Breite des zuletzt erfolgreich gerenderten Layouts – dedupliziert den Resize-Watcher gegen die
    // initiale 0→W-Messung (die createInstance schon rendert) und gegen unveränderte Breiten.
    let lastRenderedWidth = 0;
    // MusicXML, das zuletzt erfolgreich per osmd.load() geladen wurde: Re-Renders derselben Partitur
    // (Resize/Zoom) überspringen clear()+load() und initialisieren den Player NICHT neu – die
    // Wiedergabe überlebt so Container-Resizes, und Edits initialisieren nur noch einmal.
    let lastLoadedXml: string | null = null;

    // Reaktive Container-Breite (VueUse/ResizeObserver): ändert sich bei Container- UND Fenster-Resize
    // (Fenster-Resize ändert die Container-Breite ebenfalls) und treibt das Re-Render (Resize-Watcher
    // unten). Ersetzt OSMDs fenster-only autoResize und ist zugleich die einzige Breiten-Quelle für den
    // Render-Guard in render().
    const {width: containerWidth} = useElementSize(containerRef);

    function getOsmd(): OpenSheetMusicDisplay | null {
        return osmd;
    }

    // ============ Selektions-/Edit-State (geteilt zwischen den Sub-Composables) ============
    // selectedMeasure (Singular) ≠ selectedMeasures (Plural) – bewusst getrennt, NICHT redundant:
    // `selectedMeasure` = aktiver/primärer Takt (Cursor + Akkord-/Tonart-Panel-Kontext, das je auf
    // GENAU EINEM Takt arbeitet). Imperativ gesetzt bei Klick & Takt-Navigation (selectNext/
    // PreviousMeasure); ein watch in useScoreSelection wählt bei reiner Navigation automatisch eine
    // Note im neuen Takt. Kann gesetzt sein, ohne dass Noten ausgewählt sind (Klick auf leere
    // Taktfläche).
    // Interne, eindeutige Taktidentität. `selectedMeasure` bleibt absichtlich die gedruckte
    // MusicXML-Taktnummer für die bestehenden Akkord-/Tonart-/Taktart-Editor-Verträge.
    const selectedMeasureIndex = ref<number | null>(null);
    const selectedMeasure = ref<number | null>(null);
    // shallowRef: GraphicalNote ist eine komplexe OSMD-Klasseninstanz – NICHT tief reaktiv machen
    // (teuer + bricht OSMD-Interna). shallowRef behält zudem den exakten Klassentyp (kein UnwrapRef,
    // das Methoden strukturell verlöre).
    const clickedGraphicalNote = shallowRef<GraphicalNote | null>(null);
    const selectedNote = ref<NoteInfo | null>(null);
    // Mehrfachauswahl: geordnete Notenliste (dedupliziert per noteKey; enthält die primäre Note
    // `selectedNote`). Einzelauswahl = Liste der Größe 1.
    const selectedNotes = ref<NoteInfo[]>([]);
    // selectedMeasures (Plural) = ABGELEITET aus selectedNotes (watch → fullySelectedMeasures /
    // computeFullMeasures): die Takte, deren SÄMTLICHE Noten über alle Staffs ausgewählt sind. Nicht
    // imperativ setzen. Einzige Nutzung: Batch-Lösch-Unterscheidung in useScoreEditActions
    // (deleteSelection: volle Takte entfernen vs. Noten → Pausen). Siehe Kommentar bei selectedMeasure.
    const selectedMeasureIndices = ref<number[]>([]);
    const selectedMeasures = ref<number[]>([]);
    // Zwischenablage: zuletzt kopierte Note (Legacy Strg+V) + kopierte Sequenz (Mehrfach-Kopieren).
    // shallowRef genügt (reine Datenobjekte).
    const copiedSequence = shallowRef<ClipboardNote[]>([]);
    const currentMeasureChord = ref<string | null>(null);
    const currentKeySignature = ref<KeySignatureInfo | null>(null);
    const currentTimeSignature = ref<TimeSignatureInfo | null>(null);
    const activeEditTab = ref<EditTab>('note');
    // Wohin zuletzt auf die Partitur gezeigt wurde: auf eine Note oder in die freie Taktfläche.
    // Daraus baut das `NoteActionsOverlay` sein Menü. **Bewusst eigener Ref statt Ableitung aus
    // `selectedNote` (kritisch – nicht rückbauen):** der `watch(selectedMeasureIndex)` in
    // `useScoreSelection` wählt nach jedem Takt-Kontextwechsel selbst eine Note nach, und weil er
    // mit `flush: 'pre'` läuft, steht beim synchronen Öffnen des Overlays noch die Note des
    // Vortakts darin. Herleitung am Typ in `musicxml-preview/noteActionsMenu.ts`.
    const actionScope = ref<ActionScope>('note');
    // Vom Taktkanten-Doppelklick vorgewählte Seite des Struktur-Editors (Menü-/Toolbar-Pfad: null).
    const structureSide = ref<BarlineSide | null>(null);
    // Einmal-Flag: unterdrückt die Auto-Notenwahl des Takt-Watches beim programmatischen
    // Kontext-Wechsel durch insertMeasure (Details am Watch in useScoreSelection).
    const suppressMeasureAutoSelect = ref(false);

    // Struktur des ausgewählten Takts (Wiederholungen/Volta/Taktstrich), AUS DEM INHALT gelesen –
    // wie `bassStyles` bleibt der Wert so auch nach Commit/Undo/Dateiwechsel von selbst korrekt.
    const currentMeasureStructure = computed<MeasureStructureInfo | null>((oldValue) => {
        const idx = selectedMeasureIndex.value;
        const xml = toValue(musicXml);
        const next = idx === null || !xml ? null : getMeasureStructureInfo(xml, idx);
        // oldValue-Muster (Pflicht für objekt-liefernde Computeds, siehe VUE_RULES.md).
        if (next === null) return null;
        if (oldValue && JSON.stringify(oldValue) === JSON.stringify(next)) return oldValue;
        return next;
    });

    // Auftakt-Zustand von Takt 1, AUS DEM INHALT gelesen. Lazy: nur der geöffnete Takt-Editor
    // „Takte" liest ihn, ein Computed ohne Leser parst nichts.
    const pickupInfo = computed<PickupState | null>((oldValue) => {
        const xml = toValue(musicXml);
        if (!xml) return null;
        const next = pickupState(xml);
        // oldValue-Muster (Pflicht für objekt-liefernde Computeds, siehe VUE_RULES.md).
        if (oldValue && JSON.stringify(oldValue) === JSON.stringify(next)) return oldValue;
        return next;
    });

    // Zustand des Bass-Systems, AUS DEM INHALT gelesen – so stimmt er auch nach Undo und nach
    // einem Wechsel der Datei, ohne eigenen Zustand zu führen.
    const hasBassPreview = computed(() => bassPreviewStatus(toValue(musicXml) ?? '') !== 'none');
    const bassStyles = computed<Record<number, string>>((oldValue) => {
        const next = readBassPreviewStyles(toValue(musicXml) ?? '');
        // oldValue-Muster (Pflicht für objekt-liefernde Computeds, siehe VUE_RULES.md): gleiche
        // Zuordnung -> gleiche Referenz, damit Watcher nicht bei jeder Inhaltsänderung feuern.
        if (oldValue && JSON.stringify(oldValue) === JSON.stringify(next)) return oldValue;
        return next;
    });

    function setBassStyle(measureIndex: number, style: string): void {
        options.onBassStyleChange?.(measureIndex, style);
    }

    const canRebar = computed(() => !!options.onRebar);
    const rebarBlockedReason = computed(() => toValue(options.rebarBlockedReason) ?? '');

    function rebarFromMeasure(beats: number, beatType: number): void {
        const index = selectedMeasureIndex.value;
        if (index === null || rebarBlockedReason.value) return;
        options.onRebar?.(index, beats, beatType);
    }

    // Pixel-Position der angeklickten Note relativ zum Scroll-Container der Vorschau – damit der
    // Editor im NoteActionsOverlay und das BassLyricPopover an der Note schweben. `null` ⇒ keine Note.
    const selectedNoteX = ref<number | null>(null);
    const selectedNoteY = ref<number | null>(null);

    // ============ Sub-Composables ============

    const playback = useOsmdPlayback(getOsmd, () => clickedGraphicalNote.value);
    // Nur die UI-Oberfläche wird injizierbar (`ScorePlaybackContext`); der Lifecycle bleibt hier.
    // Rest-Destrukturierung statt einer handgepflegten Liste: so kann der Kontext nicht von der
    // tatsächlichen Player-Oberfläche auseinanderlaufen, und die fünf ausgeschlossenen Funktionen
    // leaken nicht zur Laufzeit an Kind-Komponenten (ein Kind darf den Player DIESER Vorschau
    // weder initialisieren noch entsorgen).
    const {
        createPlayback, bindSheet, afterRender, disposePlayer, setPlaybackStartToSelection,
        ...playbackUi
    } = playback;
    const hitTesting = useScoreHitTesting(getOsmd, containerRef);
    // Akkordsymbole und Bass-Silben: OSMD liest deren `color` nicht, sie werden nach dem
    // Render im SVG nachgefärbt (Begründung im Modul).
    const labelColors = useScoreLabelColors({getOsmd, musicXml});
    const highlight = useScoreHighlight({
        getOsmd,
        containerRef,
        scrollContainerRef,
        clickedGraphicalNote,
        selectedNoteX,
        selectedNoteY,
        // Wiederholungen der Auswahl (Pattern-Layer) – in beiden Vorschauen, Regeln in patternMatch.ts.
        findPatternMatches: (selected) => findPatternMatches(hitTesting.patternLanes(), new Set(selected)),
    });

    /** Färbt alle gerenderten Noten rot, deren Tonhöhe in `unplayableNotes` steht.
     *
     *  Muss nach jedem Render laufen (die SVG-Knoten sind dann neu) und dabei VOR
     *  `restoreSelectionHighlight()` – die Layer-Reihenfolge ist in `useScoreHighlight`
     *  begründet. Leere Liste ⇒ vorhandene Markierung abräumen. */
    function applyUnplayableHighlight(): void {
        const names = toValue(unplayableNotes);
        if (!names || names.length === 0) {
            highlight.clearUnplayableColor();
            return;
        }
        const unplayable = new Set(names);
        // Das Bass-Vorschau-System ist Prüfhilfe, keine zu spielende Melodie – seine tiefen Töne
        // liegen naturgemäß außerhalb des Diskants und dürfen nicht rot erscheinen. Die Analyse
        // klammert den Part serverseitig ohnehin aus, `names` kennt ihn also gar nicht; gefiltert
        // wird hier gegen zufällige Namensgleichheit mit einer unspielbaren Melodienote.
        const bassStaffIndices = bassPreviewStaffIndices(toValue(musicXml) ?? '');
        const hits: GraphicalNote[] = [];
        hitTesting.forEachGraphicalNote((note, staffIndex) => {
            if (bassStaffIndices.has(staffIndex)) return;
            const name = music21NameOfGraphicalNote(note);
            if (name && unplayable.has(name)) hits.push(note);
        });
        highlight.highlightUnplayableNotes(hits);
    }

    // Vorschau-Ton („so klingt die Note jetzt") beim Bearbeiten und Auswählen. Liest denselben
    // `enablePlayback`-Schalter wie der Player – aber aus einem ANDEREN Grund (nicht rückbauen):
    // in der Griffschrift-AUSGABE notiert die Partitur Knopfpositionen, die notierte Tonhöhe wäre
    // also der falsche Klang. Deshalb ein Oszillator-Ton (`notePreviewTone.ts`) statt `playSound()`:
    // er lädt KEINE SoundFonts nach, ein bloßer Notenklick stößt also keinen CDN-Download an.
    const playbackStore = usePlaybackStore();
    const previewTone: NotePreviewTone = enablePlayback
        ? (midiKeys) => {
            void playbackStore.previewPitches(midiKeys);
        }
        : NOOP_PREVIEW_TONE;

    const selection = useScoreSelection({
        getOsmd,
        musicXml,
        hitTesting,
        highlight,
        isPlaying: playback.isPlaying,
        selectedMeasureIndex,
        selectedMeasure,
        clickedGraphicalNote,
        selectedNote,
        selectedNotes,
        selectedMeasureIndices,
        selectedMeasures,
        currentMeasureChord,
        currentKeySignature,
        currentTimeSignature,
        activeEditTab,
        actionScope,
        structureSide,
        suppressMeasureAutoSelect,
        selectedNoteX,
        selectedNoteY,
        previewTone,
    });
    const editActions = useScoreEditActions({
        musicXml,
        selectedMeasureIndex,
        selectedMeasure,
        selectedNote,
        selectedNotes,
        selectedMeasureIndices,
        selectedMeasures,
        clickedGraphicalNote,
        copiedSequence,
        previewTone,
        suppressMeasureAutoSelect,
        notify: options.notify,
        enableShiftDelete: options.enableShiftDelete,
    });

    // Rubber-Band-Mehrfachauswahl: reine Gesten-Maschine (Rechteck + Treffer); die
    // Auswahl-Mutation bleibt exklusiv in `useScoreSelection.applyDragSelection`.
    const dragSelection = useScoreDragSelection({
        hitTesting,
        highlight,
        containerRef,
        scrollContainerRef,
        applyDragSelection: selection.applyDragSelection,
        restoreSelectionHighlight: selection.restoreSelectionHighlight,
    });

    // Gesangstext-Sitzung: schreibt in denselben lokalen `musicXml`-Ref wie alle Note-Edits und
    // meldet erst am Sitzungsende nach oben (siehe useLyricEntry, Festlegung 1).
    const lyricEntry = useLyricEntry({
        musicXml,
        selectedNote,
        // Beide Richtungen überspringen die restlichen Töne einer Zählzeit: die Sitzung vergibt
        // eine Silbe je Zählzeit, nicht je Akkordton (siehe useScoreSelection.selectNextNote).
        selectNextNote: () => selection.selectNextNote(true),
        selectPreviousNote: () => selection.selectPreviousNote(true),
        onSessionCommitted: options.onLyricsCommitted,
        notify: options.notify,
    });

    // ============ Instanz-Lifecycle ============

    function createInstance(el: HTMLElement): void {
        console.log('create instance')
        osmd = new OpenSheetMusicDisplay(el, {

            // autoResize AUS: Resize wird reaktiv über useElementSize(containerRef) getrieben (deckt
            // Fenster- UND Container-Größenänderungen ab – Sidebar auf/zu, Code-Split-View; OSMDs
            // eigenes autoResize reagiert NUR auf Window-Resize und deckte reine Container-Änderungen
            // nicht ab). Ein debounced Watcher auf containerWidth rendert bei Breitenänderung neu
            // (siehe unten). autoResize an zu lassen würde bei Fenster-Resize doppelt rendern
            // (OSMD-autoResize + unser Watcher).
            autoResize: false,

            coloringEnabled: true,
            pageBackgroundColor: '#ffffff',
            onXMLRead(xml: string): string {
                return xml;
            },
            useXMLMeasureNumbers: true,
            performanceMode: true,
            cursorsOptions: [
                {type: CursorType.Standard, alpha: 0.25, color: '#04b556', follow: false} as CursorOptions,
                {type: CursorType.CurrentArea, alpha: 0.18, color: '#68f7f2', follow: false} as CursorOptions,
                {type: CursorType.CurrentArea, alpha: 0.1, color: '#b9c421', follow: false} as CursorOptions,
            ],
            // followCursor AUS (kritisch – nicht auf true rückbauen): OSMDs `Cursor.reset()` ruft
            // NICHT `update()`, sondern `updateWithTimestamp()` – und dort ist das
            // `cursorElement.scrollIntoView({block:'center'})` AUSSCHLIESSLICH über `FollowCursor`
            // gegated, das per-Cursor `follow: false` oben greift dort NICHT. `reset()` läuft bei
            // jeder Notenauswahl (`positionNoteCursor`), beim Hovern rückwärts (`highlightMeasure`)
            // und nach jedem Render (`restoreSelectionHighlight`) – mit `true` scrollte der Browser
            // also bei praktisch jeder Interaktion zur Partitur. Der Playback-Cursor bewegt sich
            // trotzdem weiter (`updateWithTimestamp` positioniert ihn), nur die Seite folgt nicht.
            followCursor: false,
            drawCredits: true,
            drawLyrics: true,
            drawMeasureNumbers: true,
            drawHiddenNotes: true,
            drawPartNames: true,
            drawSubtitle: true,
            drawLyricist: true,
            autoBeam: true,
            // XML-eigene Zeilen-/Seitenumbrüche IGNORIEREN, damit OSMD die Systeme selbst auf die
            // volle Containerbreite umbricht (sonst bleibt die Partitur in den oft schmalen
            // Originalzeilen aus MuseScore/OMR).
            newPageFromXML: false,
            newSystemFromXML: false,
            newSystemFromNewPageInXML: false,
            // 'A4_P': OSMD paginiert selbst und legt PRO SEITE ein eigenes
            // `<div id="osmdCanvasPage{n}"><svg id="osmdSvgPage{n}">` in den Container – Grundlage
            // des seitenweisen PDF-Exports. Achtung: alle Seiten teilen sich denselben
            // Koordinatenursprung, Klick-/Hover-Auflösung muss deshalb seitenbewusst rechnen
            // (siehe `osmd/pageSvgs.ts`).
            pageFormat: 'A4_P',
            coloringMode: ColoringModes.XML,
            drawTitle: true,
            drawComposer: true,
            drawMetronomeMarks: true,
            defaultColorLabel: 'gray',
            backend: 'SVG',
            // SkyBottomLine-Batch deaktivieren: der Batch-Calculator legt ein Canvas an und ruft
            // getImageData(0,0,w,h) auf (Fläche = Breite × zoom × devicePixelRatio). Bei ≥5 Takten +
            // HiDPI/hohem Zoom führt das zu "Out of memory at ImageData creation" (Chrome pausiert
            // präventiv, kein fangbarer JS-Fehler). Ein sehr hoher Schwellwert erzwingt die per-Takt-
            // Berechnung → im performanceMode reiner SkyBottomLineCalculatorSVG (kein getImageData).
            skyBottomLineBatchMinMeasures: Number.MAX_SAFE_INTEGER,
        });
        // 'warn' statt 'debug': OSMD loggt sonst pro Render/Playback-Tick auf dem Main-Thread – der
        // osmd-extended Scheduler hat keinen Lookahead, jede Konsolenausgabe wird hörbare Verspätung.
        osmd.setLogLevel('warn');
        // EINZIGER Gate für die Wiedergabe (kritisch – nicht rückbauen): ohne `createPlayback`
        // existiert kein `PlaybackManager`, und der `ensureSoundsLoaded(CORE_SOUNDFONTS)`-Aufruf
        // darin entfällt – die MB-großen SoundFonts werden für die Griffschrift-Ausgabe also gar
        // nicht erst geladen. `bindSheet()`/`play()` prüfen bereits `if (!playback) return`,
        // `afterRender()` hängt an `isPlaying` (bleibt false) – deshalb genügt dieser eine Gate.
        // `useOsmdPlayback` wird trotzdem UNBEDINGT aufgerufen (Composables nie bedingt aufrufen).
        if (enablePlayback) createPlayback(osmd);

        // OSMDs eigene DOM-Interaktion abschalten (siehe disableOsmdInternalInteraction): der interne
        // WebDisplayInteractionManager crasht sonst bei Maus-Events auf noch nicht ausgelegter Partitur
        // ("reading '0'" = MusicSystems[0]). Wir behandeln Klicks/Zoom ohnehin selbst.
        disableOsmdInternalInteraction(osmd);
        disableOsmdCursorScrolling(osmd);
    }

    /** Die EINE Post-Render-Sequenz (nach jedem `osmd.render()`): interne DOM-Listener abhängen,
     *  Player binden, Cursor-Scroll neutralisieren, Farb-Layer + Auswahl wiederherstellen.
     *  Reihenfolge ist tragend: `disableOsmdCursorScrolling` erst NACH `bindSheet`/`afterRender`
     *  (beide können über enableOrDisableCursors neue Cursor anlegen) und VOR
     *  `restoreSelectionHighlight` (erster Cursor-Positionierer und damit erster möglicher
     *  Scroll nach dem Render). */
    function postRender(osmdValue: OpenSheetMusicDisplay): void {
        // Ein Re-Render (Zoom, Edit, Resize) invalidiert die Content-Koordinaten eines laufenden
        // Rubber-Band-Drags – sauber abbrechen statt ein Geister-Rechteck weiterzuziehen.
        dragSelection.cancelDrag();
        disableOsmdInternalInteraction(osmdValue);
        // Player genau einmal pro geladenem Sheet binden (idempotent); danach nur den
        // Render-Hook (Cursor bei laufender Wiedergabe wieder einblenden).
        bindSheet();
        afterRender();
        disableOsmdCursorScrolling(osmdValue);
        applyUnplayableHighlight();
        labelColors.applyLabelColors();
        selection.restoreSelectionHighlight();
        // Mehrteilig? Erst nach render() ist GraphicSheet.MeasureList gebaut. Bei jedem
        // Score-Wechsel (Movement-Swap) neu berechnet; steuert die Part-Erweiterungs-Buttons.
        hasMultipleParts.value = hitTesting.getAllStaffIndices().length > 1;
    }

    async function render(): Promise<void> {
        const xml = toValue(musicXml);
        const osmdValue = osmd;
        if (!osmdValue || !xml) return;

        error.value = null;
        try {
            // Nur bei geändertem Inhalt neu laden (sonst reines Re-Layout, z. B. Resize).
            const needsLoad = xml !== lastLoadedXml || !osmdValue.Sheet;
            if (needsLoad) {
                lastLoadedXml = null;
                osmdValue.clear();
                await osmdValue.load(xml);
                lastLoadedXml = xml;
            }

            // OSMD nur rendern, wenn der Container eine echte Breite hat. Ein Render bei Breite 0
            // (initialer Mount, noch nicht ausgelegtes/unsichtbares Layout) gibt jedem VexFlow-Stave
            // Breite 0 → "SkyBottomLineBatchCalculatorBackend: width not > 0"-Warnung pro Takt. Fehlt
            // die Breite noch, holt das nächste Render (musicXml-/zoom-Watcher bzw. der Resize-Watcher
            // auf containerWidth, sobald die Breite > 0 wird) die Darstellung nach.
            if (containerWidth.value > 0) {
                // Zoom VOR dem Render setzen – ein nachgelagertes Zoom-Setzen erzwänge ein
                // zweites volles Render (= zwei SkyBottomLine-Pässe back-to-back und kurzfristig
                // doppelter Speicherdruck). Schmale Container deckeln den Zoom (`effectiveZoom`);
                // der Resize-Watcher unten rendert beim Überschreiten der Schwelle ohnehin neu.
                osmdValue.zoom = effectiveZoom(containerWidth.value, toValue(zoom));
                await osmdValue.render();
                // Breite merken, unter der zuletzt gerendert wurde (dedupliziert den Resize-Watcher).
                lastRenderedWidth = containerWidth.value;
                postRender(osmdValue);
            }
        } catch (e) {
            console.error('[MusicXML Preview] Failed to render file', e);
            error.value = e instanceof Error ? e.message : String(e);
        }
    }

    // ============ Watches & Lifecycle ============

    // Create the instance once the container is mounted, then render the current content.
    watchDebounced(containerRef, (el) => {
        if (!el || osmd) return;
        createInstance(el);
        void render();
    }, {debounce: 30});

    // Re-render (debounced) whenever the MusicXML changes. 200 ms statt 20 ms: im Live-Quelltext-
    // Editor tippt der Nutzer – ein volles OSMD-Render pro Tastendruck ist unnötig teuer.
    watchDebounced(
        () => toValue(musicXml),
        () => {
            void render();
        },
        {debounce: 200},
    );

    // Container-/Fenster-Resize (VueUse): bei geänderter Breite neu rendern. Ersetzt OSMDs autoResize
    // (das nur auf Window-Resize reagiert) und schließt die Container-Resize-Lücke (Sidebar/Split-View).
    // Debounced, weil ein OSMD-Render teuer ist. Zusätzlich holt es den initialen Render nach, falls der
    // Container mit Breite 0 mountete (createInstance-Render dann übersprungen) und erst später Breite
    // bekommt.
    watchDebounced(containerWidth, (w) => {
        // !osmd: vor Instanz-Erzeugung ignorieren (die containerRef-Watch rendert initial).
        // w <= 0: kein Render bei verstecktem/0-breitem Container.
        // w === lastRenderedWidth: dedupliziert die initiale 0→W-Messung + unveränderte Breiten.
        if (!osmd || w <= 0 || w === lastRenderedWidth) return;
        void render();
    }, {debounce: 150});

    // Zoom reaktiv anwenden. Bewusst über render(): das setzt den Zoom VOR dem Render, überspringt
    // bei unverändertem XML das Neuladen und bringt die Guards mit (containerWidth > 0, try/catch),
    // die dem früheren applyZoom() fehlten – das konnte mitten in einem laufenden load() auf einer
    // leeren Partitur rendern.
    watch(() => toValue(zoom), () => {
        void render();
    });

    // Neue Analyse ⇒ nur umfärben, kein Re-Render (das Notenbild ist unverändert). Das Auswahl-
    // Highlight wird danach neu aufgesetzt, damit es über dem Rot-Layer liegt.
    watch(() => toValue(unplayableNotes), () => {
        applyUnplayableHighlight();
        selection.restoreSelectionHighlight();
    });

    // Playback-Startpunkt folgt der zuletzt ausgewählten Note: solange nicht abgespielt wird,
    // stehen Playback-Iterator + Cursor auf der Primärauswahl. Während der Wiedergabe bleibt der
    // laufende Transport unberührt (Resume an aktueller Stelle).
    watch(clickedGraphicalNote, () => {
        if (!playback.isPlaying.value) setPlaybackStartToSelection();
    });

    // Chord-/Key-Panel synchron halten – in ZWEI Watches entkoppelt (Perf):
    // 1) Taktwechsel → sofort neu laden (sonst zeigt der Akkord-Editor den alten Takt).
    //
    // LAZY (nicht rückbauen): nur parsen, wenn der zugehörige Takt-Editor OFFEN ist – und dann nur
    // dessen Wert. Vorher parste JEDER Taktwechsel (jeder Notenklick setzt selectedMeasureIndex)
    // das komplette XML dreimal (Akkord, Tonart, Taktart), auch ohne offenen Editor. Beim Öffnen
    // laden open{Chord,KeySignature,TimeSignature}Editor den Wert ohnehin frisch; hier geht es nur
    // um den OFFEN bleibenden Editor bei Taktwechsel (ChordEditView-Auto-Advance) und Inhalts-Edits.
    function reloadMeasurePanels(
        measureIndex: number | null,
        measureNumber: number | null,
        xml: string | null,
    ): void {
        const tab = activeEditTab.value;
        if (tab !== 'chord' && tab !== 'key' && tab !== 'time') return;
        if (measureNumber === null || !xml) {
            currentMeasureChord.value = null;
            currentKeySignature.value = null;
            currentTimeSignature.value = null;
            return;
        }
        const ordinal = measureIndex ?? undefined;
        if (tab === 'chord') {
            currentMeasureChord.value = getChordFromMeasure(xml, measureNumber, undefined, ordinal);
        } else if (tab === 'key') {
            currentKeySignature.value = getKeySignatureFromMeasure(xml, measureNumber, undefined, ordinal);
        } else {
            currentTimeSignature.value = getTimeSignatureFromMeasure(xml, measureNumber, undefined, ordinal);
        }
    }

    watch(
        [selectedMeasureIndex, selectedMeasure],
        ([measureIndex, measureNumber]) => reloadMeasurePanels(measureIndex, measureNumber, toValue(musicXml)),
    );
    // 2) Inhalts-Edits → debounced neu laden. Ein sofortiger Re-Parse pro Tastendruck im
    //    Quelltext-Editor würde das komplette XML zweimal parsen (Akkord + Tonart) – gedrosselt
    //    reicht völlig (die Panels zeigen nur den Zustand des ausgewählten Takts).
    watchDebounced(
        () => toValue(musicXml),
        (xml) => reloadMeasurePanels(selectedMeasureIndex.value, selectedMeasure.value, xml),
        {debounce: 250},
    );

    // Beim Scrollen der Vorschau die Position der Note nachziehen (Note wandert mit dem Inhalt; der
    // Editor im NoteActionsOverlay und das BassLyricPopover hängen an `selectedNoteX/Y`).
    // Während der Wiedergabe NICHT pro Scroll-Event (zwei getBoundingClientRect = erzwungenes Layout auf
    // dem Main-Thread, den der Lookahead-freie Audio-Scheduler braucht); einmal nachziehen, sobald die
    // Wiedergabe endet.
    useEventListener(scrollContainerRef, 'scroll', () => {
        // Der Tonhöhen-Tooltip klebt am Zeiger, der Notentext scrollt darunter weg – der Inhalt
        // wäre danach stale. Verwerfen statt nachrechnen: drei Ref-Schreibzugriffe, KEIN
        // getBoundingClientRect, deshalb anders als `computeSelectedNotePosition` auch während der
        // Wiedergabe unbedenklich. Der nächste `mousemove` baut ihn korrekt wieder auf.
        selection.clearHover();
        // Laufender Rubber-Band-Drag: die Content-Startecke ist mit dem Inhalt gewandert –
        // Rechteck + Treffer zur unveränderten Zeigerposition neu berechnen (rAF-gebündelt).
        dragSelection.refreshDragFrame();
        if (!playback.isPlaying.value) highlight.computeSelectedNotePosition();
    }, {passive: true});

    // Im Vorschau-Scroller scrollt ausschließlich der Nutzer: programmatisches Scrollen AUF dem
    // Container wird verworfen (siehe shared/scrollLock.ts). Nutzer-Scrollen läuft im Browser an
    // diesen APIs vorbei und bleibt unberührt; die Getter liefern weiter die echte Position, damit
    // der Scroll-Listener oben und computeSelectedNotePosition korrekt bleiben.
    watch(scrollContainerRef, (el, _previous, onCleanup) => {
        if (!el) return;
        onCleanup(lockScrollContainer(el));
    }, {immediate: true});
    watch(playback.isPlaying, (playing) => {
        if (!playing) highlight.computeSelectedNotePosition();
    });

    onBeforeUnmount(() => {
        disposePlayer();
        try {
            osmd?.clear();
        } catch {
            /* ignore */
        }
        // free the space
        try {
            osmd?.dispose();
        } catch {
            /* ignore */
        }
        osmd = null;
    });

    // ============ Kontext bereitstellen (provide/inject) ============

    // Frisches Objektliteral statt `Object.assign({}, selection, {…})` (kritisch – nicht
    // rückbauen): nur bei einem Literal greift TypeScripts Excess-Property-Prüfung. Der Spread
    // schmuggelte sonst `selectNote`/`restoreSelectionHighlight` undeklariert in den Kontext –
    // bei `Object.assign` ist der Typ eine Intersection, TS kann das nie fangen. Die Member aus
    // `selection` stehen deshalb jetzt explizit hier.
    const context: ScoreEditContext = {
        selectedMeasureIndex,
        selectedMeasure,
        getOsmd,

        selectedNote,
        selectedNotes,
        selectedMeasures,
        selectedNoteX,
        selectedNoteY,
        currentChord: currentMeasureChord,
        currentKeySignature,
        currentTimeSignature,
        activeTab: activeEditTab,
        hasBassPreview,
        bassStyles,
        setBassStyle,

        hasMultipleParts,
        openChordEditor: selection.openChordEditor,
        openKeySignatureEditor: selection.openKeySignatureEditor,
        openTimeSignatureEditor: selection.openTimeSignatureEditor,
        openBassStyleEditor: selection.openBassStyleEditor,
        currentMeasureStructure,
        structureSide,
        openStructureEditor: selection.openStructureEditor,
        openMeasuresEditor: selection.openMeasuresEditor,
        setRepeat: editActions.setRepeat,
        setRepeatTimes: editActions.setRepeatTimes,
        setBarStyle: editActions.setBarStyle,
        setEnding: editActions.setEnding,
        removeEnding: editActions.removeEnding,
        insertMeasure: editActions.insertMeasure,
        deleteMeasure: editActions.deleteMeasure,
        pickupInfo,
        setPickup: editActions.setPickup,
        // **Als Pfeilfunktion, nie als nackte Referenz (kritisch – nicht rückbauen).** Die
        // `ScoreEditToolbar` bindet beide als `@click="selectNextNote"`; Vue übergäbe dann das
        // `MouseEvent` als `skipSamePosition`, und der Toolbar-Pfeil überspränge stillschweigend
        // Akkordtöne (die Pfeiltaste ruft explizit auf und täte es nicht). `editContext` deklariert
        // `() => void`, `vue-tsc` fängt das also nicht. Guard: `sourceGuards.spec.ts`.
        selectNextNote: () => selection.selectNextNote(),
        selectPreviousNote: () => selection.selectPreviousNote(),
        extendSelectionByNote: selection.extendSelectionByNote,
        extendSelectionByMeasure: selection.extendSelectionByMeasure,
        extendSelectionByPart: selection.extendSelectionByPart,
        activeVerse: lyricEntry.activeVerse,
        isLyricActive: lyricEntry.isLyricActive,
        lyricDraft: lyricEntry.lyricDraft,
        commitLyric: lyricEntry.commitLyric,
        endLyricEntry: lyricEntry.endLyricEntry,
        selectNextMeasure: selection.selectNextMeasure,
        clearSelection: selection.clearSelection,
        // Pfeilfunktion aus demselben Grund wie oben: als `@click` gebunden käme sonst das
        // `MouseEvent` als `opts` an.
        requestDelete: (opts?: {shift?: boolean}) => editActions.requestDelete(opts),
        deleteChoicePending: editActions.deleteChoicePending,
        resolveDeleteChoice: editActions.resolveDeleteChoice,
        splitNote: editActions.splitNote,
        mergeNote: editActions.mergeNote,
        mergeTiedNotes: editActions.mergeTiedNotes,
        halveDurations: editActions.halveDurations,
        doubleDurations: editActions.doubleDurations,
        copiedSequence,
        copySelection: editActions.copySelection,
        pasteNoteReplace: editActions.pasteNoteReplace,
        pasteSelection: editActions.pasteSelection,
        transposeSelection: editActions.transposeSelection,
        addDiatonicTones: editActions.addDiatonicTones,
        reduceTriads: editActions.reduceTriads,
        removeExtremeChordTones: editActions.removeExtremeChordTones,
        toggleTie: editActions.toggleTie,
        saveChord: editActions.saveChord,
        removeChord: editActions.removeChord,
        saveKeySignature: editActions.saveKeySignature,
        saveTimeSignature: editActions.saveTimeSignature,
        checkTimeSignatureOverfull: (
            measureNumber: number, beats: number, beatType: number, measureIndex?: number,
        ) => {
            const xml = toValue(musicXml);
            return !!xml && measureIsOverfullForMeter(
                xml, measureNumber, beats, beatType, measureIndex,
            );
        },
        canRebar,
        rebarBlockedReason,
        rebarFromMeasure,
    };
    provideScoreEditContext(context);
    // Player-Kontext NUR wenn diese Vorschau überhaupt einen Player hat (Eingabe-Vorschau). Ohne
    // Provider injizieren `PlaybackControlsOverlay` (nullable) und `OsmdPlaybackControls` nichts – die
    // Bedienleiste erscheint auf der Griffschrift-Ausgabe gar nicht erst.
    if (enablePlayback) provideScorePlayback(playbackUi);

    // Rückgabewert ≠ bereitgestellter Kontext (kritisch – nicht wieder zusammenlegen): was hier
    // steht, braucht ausschließlich der HOST (`MusicXmlPreview`) für sein Template, seine globalen
    // Tastenkürzel und sein `defineExpose`. Die DOM-Handler, `error` und der Player gehören
    // deshalb NICHT in den injizierbaren `ScoreEditContext` – dort stünden 7 Member, die kein
    // Kind je injiziert.
    return {
        // Host-eigene DOM-Verdrahtung + Fehlerbox.
        error, getOsmd,
        handleContainerClick: selection.handleContainerClick,
        // Ungedrosselt durchreichen: `handleContainerHover` coalesced selbst per
        // requestAnimationFrame (ein Render pro Frame, jüngstes Event gewinnt). Ein zusätzliches
        // Debounce davor machte das rAF wirkungslos und den Hover nur träger.
        // GEGATET während eines Rubber-Band-Drags (kein Debounce): die Live-Vorschau des Drags
        // besitzt dann den Hover-Layer, und Pointer Capture feuert weiterhin mousemove/mouseleave
        // – ungegatet malte der Hover dazwischen bzw. `mouseleave` löschte die Live-Vorschau.
        handleContainerHover: (event: MouseEvent) => {
            if (dragSelection.isDragSelecting.value) return;
            selection.handleContainerHover(event);
        },
        handleContainerMouseLeave: () => {
            if (dragSelection.isDragSelecting.value) return;
            selection.handleContainerMouseLeave();
        },
        handleContainerContextMenu: selection.handleContainerContextMenu,
        // Rubber-Band-Mehrfachauswahl: Pointer-Verdrahtung + Rechteck-Div des Hosts.
        handleContainerPointerDown: dragSelection.handleContainerPointerDown,
        handleContainerPointerMove: dragSelection.handleContainerPointerMove,
        handleContainerPointerUp: dragSelection.handleContainerPointerUp,
        // pointercancel (Browser übernimmt die Geste) bricht ab – NIEMALS committen: ein
        // Halb-Drag darf die Auswahl nicht ersetzen.
        cancelDragSelection: dragSelection.cancelDrag,
        dragRect: dragSelection.dragRect,
        isDragSelecting: dragSelection.isDragSelecting,
        consumeDragClick: dragSelection.consumeDragClick,
        // Datenbasis des Tonhöhen-Tooltips. Gehört aus demselben Grund wie die DOM-Handler NICHT in
        // den `ScoreEditContext`: den Tooltip rendert ausschließlich der Host, kein Kind injiziert ihn.
        hoveredNote: selection.hoveredNote,
        hoverX: selection.hoverX,
        hoverY: selection.hoverY,
        // Read-only-Spiegel für die Gates des Hosts (Griff/Bass/Lyric-Werkzeuge).
        selectedMeasure, selectedNote, selectedNotes,
        clearSelection: selection.clearSelection,
        isLyricActive: lyricEntry.isLyricActive,
        startLyricEntry: lyricEntry.startLyricEntry,
        // Lösch-Dialog offen: der Host unterdrückt damit den Tonhöhen-Tooltip (der Backdrop
        // verschluckt `mousemove`). Den Dialog selbst bedient der Kontext.
        deleteChoicePending: editActions.deleteChoicePending,
        // Aktionsmenü des Hosts: öffnet sich, sobald ein Takt-Editor aktiv wird (Toolbar), und
        // verankert sich im Editor-Modus an der Note. `actionScope` steuert, WELCHE Einträge es
        // zeigt – bewusst hier und nicht im `ScoreEditContext`: das Overlay bekommt seine übrigen
        // Sichtbarkeits-Eingaben (showGrip/showBass/showLyric/showMeasureEdits) ebenfalls als Prop.
        activeEditTab, actionScope, selectedNoteX, selectedNoteY,
        // Player für den Leertaste-Handler des Hosts. Ohne `enablePlayback` ist das ein No-op
        // (kein PlaybackManager) – der Host gated zusätzlich selbst.
        playback: playbackUi,
    } as const;
}

/** Rückgabetyp des Orchestrators – NICHT `ScoreEditContext` (siehe Kommentar an `return`). */
export type OsmdInteractiveScore = ReturnType<typeof useOsmdInteractiveScore>;
